import { MoreHorizontal, Plus, Star } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { OrganisationCombobox, type OrganisationChoice } from '../components/OrganisationCombobox'
import { todayIso } from '../dates'
import { CrmApiError } from '../http'
import {
  addMember,
  createOrganisation,
  matchOrganisations,
  removeMember,
  restoreMember,
  updateMember,
} from '../organisations/api'
import type { CrmContactDetail, CrmMembership } from '../types'

const menuItemClass =
  'rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50'

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback
}

/**
 * The actions of one membership. The menu closes when an item is chosen, and Radix puts the
 * focus back on its button. While another change is on its way the items wait; the button
 * itself stays enabled, or the focus could not go back to it.
 */
function MembershipMenu({
  membership,
  busy,
  run,
  unlink,
}: {
  membership: CrmMembership
  busy: boolean
  run: (action: () => Promise<unknown>, success: string) => Promise<void>
  unlink: (membership: CrmMembership) => Promise<void>
}) {
  const [open, setOpen] = useState(false)

  function choose(action: () => Promise<void>) {
    setOpen(false)
    void action()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label={`Acties voor ${membership.organisation_name}`}
        >
          <MoreHorizontal />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-52 flex-col p-1">
        {!membership.ended_on && !membership.is_primary ? (
          <button
            type="button"
            className={menuItemClass}
            disabled={busy}
            onClick={() =>
              choose(() =>
                run(
                  () => updateMember(membership.id, { is_primary: true }),
                  `${membership.organisation_name} is nu de primaire organisatie.`,
                ),
              )
            }
          >
            Primair maken
          </button>
        ) : null}
        {membership.ended_on ? (
          <button
            type="button"
            className={menuItemClass}
            disabled={busy}
            onClick={() =>
              choose(() =>
                run(
                  () => updateMember(membership.id, { ended_on: null }),
                  `Weer huidig bij ${membership.organisation_name}.`,
                ),
              )
            }
          >
            Weer huidig maken
          </button>
        ) : (
          <button
            type="button"
            className={menuItemClass}
            disabled={busy}
            onClick={() =>
              choose(() =>
                run(
                  () => updateMember(membership.id, { ended_on: todayIso() }),
                  `Voormalig bij ${membership.organisation_name}.`,
                ),
              )
            }
          >
            Voormalig maken
          </button>
        )}
        <button
          type="button"
          className={cn(menuItemClass, 'text-destructive')}
          disabled={busy}
          onClick={() => choose(() => unlink(membership))}
        >
          Ontkoppelen
        </button>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Card "Organisaties" in the right column of the contact page (spec-63): current memberships
 * first, the primary one marked, former ones grey. Link with a role ("+"), make primary or
 * former, unlink with undo. Every change reloads the contact: the primary membership decides
 * "Functie" and the legacy organisation text. The page can ask for the link form too (Meer ▾,
 * "Koppelen aan organisatie"): every `linkRequest` other than the last one this card saw (0
 * until the first) opens it, also one that was already there when the card appeared on a phone.
 */
export function OrganisationsCard({
  detail,
  onChanged,
  linkRequest,
}: {
  detail: CrmContactDetail
  onChanged: () => void
  linkRequest: number
}) {
  const [linking, setLinking] = useState(false)
  const [seenRequest, setSeenRequest] = useState(0)
  // One change at a time: while one is on its way the menu items that start one wait.
  const [busy, setBusy] = useState(false)
  const working = useRef(false)
  const linkButton = useRef<HTMLButtonElement>(null)
  const wasLinking = useRef(false)
  const currentCount = detail.organisations.filter((membership) => !membership.ended_on).length

  // "Adjusting state when a prop changes": the form opens for each new, non-zero request.
  if (linkRequest !== seenRequest) {
    setSeenRequest(linkRequest)
    if (linkRequest !== 0) setLinking(true)
  }

  useEffect(() => {
    // The form takes the focus when it opens; the button that opened it gets it back.
    if (wasLinking.current && !linking) linkButton.current?.focus()
    wasLinking.current = linking
  }, [linking])

  /** Runs one change and reports a failure; a change asked for during another is dropped. */
  async function change(action: () => Promise<void>, failure: string) {
    if (working.current) return
    working.current = true
    setBusy(true)
    try {
      await action()
    } catch (caught) {
      toast.error(errorText(caught, failure))
    } finally {
      working.current = false
      setBusy(false)
    }
  }

  function run(action: () => Promise<unknown>, success: string) {
    return change(async () => {
      await action()
      toast.success(success)
      onChanged()
    }, 'Opslaan mislukt.')
  }

  function unlink(membership: CrmMembership) {
    return change(async () => {
      await removeMember(membership.id)
      onChanged()
      toastUndo(`Ontkoppeld van ${membership.organisation_name}.`, async () => {
        await restoreMember(membership.id)
        onChanged()
      })
    }, 'Ontkoppelen mislukt.')
  }

  return (
    <section aria-label="Organisaties" className="vonk-card flex flex-col gap-2 p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Organisaties ({currentCount})</h2>
        <Button
          ref={linkButton}
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Organisatie koppelen"
          onClick={() => setLinking(true)}
        >
          <Plus />
        </Button>
      </header>
      {detail.organisations.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen organisatie.</p>
      ) : (
        <ul aria-label="Organisaties" className="flex flex-col gap-1">
          {detail.organisations.map((membership) => (
            <li key={membership.id} className="flex items-center gap-2 text-sm">
              <EntityAvatar kind="company" name={membership.organisation_name} size="xs" />
              <span
                className={cn(
                  'flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5',
                  membership.ended_on && 'text-muted-foreground',
                )}
              >
                <Link
                  to={`/relaties/organisaties/${membership.organisation_id}`}
                  className="truncate font-medium underline-offset-2 hover:underline"
                >
                  {membership.organisation_name}
                </Link>
                {membership.role ? (
                  <span className="text-muted-foreground">· {membership.role}</span>
                ) : null}
                {membership.is_primary ? (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <Star className="size-3.5 fill-amber-400 text-amber-500" aria-hidden />
                    primair
                  </span>
                ) : null}
                {membership.ended_on ? <span className="text-xs">(voormalig)</span> : null}
              </span>
              <MembershipMenu membership={membership} busy={busy} run={run} unlink={unlink} />
            </li>
          ))}
        </ul>
      )}
      {linking ? (
        <LinkOrganisationForm
          personId={detail.person_id}
          onLinked={() => {
            setLinking(false)
            onChanged()
          }}
          onCancel={() => setLinking(false)}
        />
      ) : null}
    </section>
  )
}

/**
 * Link an existing or a new organisation with a role. A new name first looks for an
 * organisation with the same name key ("Acme" = "ACME B.V."), like `organisation_name`
 * in "Nieuw contact"; otherwise it is created. Either way it is remembered as
 * existing, so a second try (founder confirmation, a failed link) cannot create it twice.
 */
function LinkOrganisationForm({
  personId,
  onLinked,
  onCancel,
}: {
  personId: string
  onLinked: () => void
  onCancel: () => void
}) {
  const [choice, setChoice] = useState<OrganisationChoice | null>(null)
  const [role, setRole] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [founderQuestion, setFounderQuestion] = useState<string | null>(null)
  /** Set at once, before the next render: a second submit has to find the first one on its way. */
  const submitting = useRef(false)

  async function link(confirmFounder = false) {
    if (submitting.current) return
    if (!choice) {
      setError('Kies een organisatie.')
      return
    }
    submitting.current = true
    setBusy(true)
    setError(null)
    try {
      let target = choice
      if (target.kind === 'new') {
        const matches = await matchOrganisations({ name: target.name })
        const sameName = matches.find((match) => match.reason === 'name')
        const organisation = sameName ?? (await createOrganisation({ name: target.name }))
        target = { kind: 'existing', id: organisation.id, name: organisation.name }
        setChoice(target)
      }
      await addMember(target.id, {
        person_id: personId,
        role: role.trim() || null,
        confirm_founder: confirmFounder || undefined,
      })
      toast.success(`Gekoppeld aan ${target.name}.`)
      onLinked()
    } catch (caught) {
      // The question is about the contact, so it stays when another organisation is chosen.
      if (caught instanceof CrmApiError && caught.code === 'founder_confirmation_required') {
        setFounderQuestion(caught.message)
      } else {
        setError(errorText(caught, 'Koppelen mislukt.'))
      }
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    void link()
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <OrganisationCombobox
        value={choice}
        onChange={(next) => {
          setChoice(next)
          setError(null)
        }}
        autoFocus
      />
      <Input
        value={role}
        onChange={(event) => {
          setRole(event.target.value)
          setError(null)
        }}
        aria-label="Rol"
        placeholder="Rol, bijv. CFO"
      />
      {founderQuestion ? (
        <div role="alert" className="rounded-lg border border-warning-border bg-warning-subtle p-3 text-sm">
          <p>{founderQuestion}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            disabled={busy}
            onClick={() => void link(true)}
          >
            Toch koppelen
          </Button>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? 'Bezig…' : 'Koppelen'}
        </Button>
      </div>
    </form>
  )
}
